/**
 * Shared print template for the Rapports & Analyses pages.
 *
 * Each Reports page calls `printReportDocument({ title, contentHtml, ... })`
 * which opens a new window with the standard UNIVERS COSMETIC chrome:
 *   - Running header (logo + brand + tagline + report title + period) on every page
 *   - Running footer with the SAS legal text on the last page (in-flow)
 *   - Bottom-left "Page X of Y"
 *   - Bottom-right "Merci pour votre confiance." amber italic
 *
 * Reports just supply their `contentHtml` (typically a <table> + summary block).
 *
 * Mirrors the chrome of invoices/quotations (see printDoc.js) — kept in a
 * separate file so report-specific styling stays decoupled from the invoice.
 */
import { openPrintWindow } from './openPrintWindow';


const LOGO_URL =
    process.env.REACT_APP_BRAND_LOGO_PRIMARY ||
    'https://pub-391b3b7615324f2a876915def2e4b4cb.r2.dev/brand/logo-primary.jpg';


const COMPANY = {
    facebook: '/UniCosm',
    instagram: '@UniCosm',
    phone: '+33 (0) 1 47 43 28 12',
    email: 'bonjour@universcosmetic.eu',
    website: 'www.universcosmetic.eu',
    tagline: 'PARTENAIRE DE CONFIANCE EN DISTRIBUTION BEAUTÉ',
    legal_line_1: 'SAS UNIVERS COSMETIC, 21 RUE EUGENE HENAFF, 93000 BOBIGNY, FRANCE.',
    legal_line_2:
        'SIEGE SOCIAL : 89302923100014 RCS BOBIGNY, TVA INTRACOM : FR85893029231, EORI N° FR89302923100014, APE : 4775Z',
    legal_line_3: 'IBAN : FR76 3000 3028 3900 0200 3387 119, BIC - SWIFT : SOGEFRPP',
};

const ICON = {
    facebook: '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>',
    phone: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.7 2Z"/></svg>',
    email: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>',
};

const LABELS = {
    fr: { page: 'Page', of: 'sur', thanks: 'Merci pour votre confiance.' },
    en: { page: 'Page', of: 'of', thanks: 'Thank you for your trust.' },
};

const safe = (s) => (s == null ? '' : String(s));

/**
 * Builds the full HTML for a printable report document.
 *
 * @param {object}  opts
 * @param {string}  opts.title         Big title rendered top-right (eg. "RELEVÉ DE COMPTE CLIENT")
 * @param {string}  opts.contentHtml   Main report body (table + summary), inserted in <tbody><td>.
 * @param {string} [opts.subtitle]     Small line under the title (eg. period range)
 * @param {string} [opts.language]     'fr' | 'en' (defaults to 'fr')
 * @param {string} [opts.metaHtml]     Additional client / context block rendered below the contact strip
 */
export function buildReportHtml(opts) {
    const language = opts.language || 'fr';
    const L = LABELS[language.slice(0, 2)] || LABELS.fr;
    const title = safe(opts.title);
    const subtitle = safe(opts.subtitle);
    const contentHtml = opts.contentHtml || '';
    const metaHtml = opts.metaHtml || '';

    return `<!DOCTYPE html>
<html lang="${language}">
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  @page {
    size: A4;
    margin: 14mm 12mm 18mm 12mm;
    @bottom-left {
      content: "${L.page} " counter(page) " ${L.of} " counter(pages);
      font-family: 'Century Gothic','Manrope','DM Sans',sans-serif;
      font-size: 9px;
      font-style: italic;
      color: #888;
    }
    @bottom-right {
      content: "${L.thanks}";
      font-family: 'Century Gothic','Manrope','DM Sans',sans-serif;
      font-size: 9px;
      color: #C08478;
      font-style: italic;
    }
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Century Gothic','Manrope','DM Sans',Arial,sans-serif;
    color: #1A1A1A;
    font-size: 11px;
    line-height: 1.45;
    -webkit-font-smoothing: antialiased;
    /* Stretch the body to the page height so the legal-footer (which uses
       margin-top: auto inside this flex column) sits at the very bottom of
       the LAST page even when content is short. */
    min-height: 265mm;          /* A4 297mm − 14mm top − 18mm bottom */
    display: flex;
    flex-direction: column;
  }
  table.page-frame { width: 100%; border-collapse: collapse; }

  /* Running header repeated on every page via <thead> on the outer table. */
  table.page-frame { width: 100%; border-collapse: collapse; }
  table.page-frame > thead { display: table-header-group; }
  table.page-frame > thead > tr > td,
  table.page-frame > tbody > tr > td { padding: 0; border: 0; }
  .page-header { background: #fff; padding-bottom: 18px; }

  .hdr-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding: 0 0 10px 0;
    border-bottom: 1.5px solid #1A1A1A;
  }
  .brand { display: flex; align-items: center; gap: 14px; }
  .brand img { width: 58px; height: 58px; object-fit: contain; }
  .brand-text { display: inline-block; }
  .brand h1 {
    margin: 0;
    font-size: 19px;
    font-weight: 600;
    letter-spacing: 2px;
    line-height: 1;
    display: inline-block;
  }
  .brand h1 span { font-weight: 300; letter-spacing: 4px; }
  .brand .tagline {
    color: #888;
    font-size: 7.5px;
    letter-spacing: 0.6px;
    margin-top: 6px;
    text-transform: uppercase;
    max-width: 230px;
    line-height: 1.45;
    text-align: center;
  }

  .doc-meta { text-align: right; min-width: 220px; }
  .doc-meta .title {
    font-size: 19px;
    letter-spacing: 4px;
    color: #C08478;
    font-weight: 600;
    line-height: 1.1;
    text-transform: uppercase;
  }
  .doc-meta .subtitle {
    margin-top: 4px;
    color: #555;
    font-size: 10px;
  }
  .doc-meta .gen {
    color: #888;
    font-size: 9.5px;
    margin-top: 3px;
  }

  .info-strip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 18px;
    padding: 7px 0 0 0;
    color: #555;
    font-size: 9.5px;
  }
  .info-strip .group { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .info-strip .item { display: inline-flex; align-items: center; gap: 5px; color: #555; white-space: nowrap; }
  .info-strip .item svg { color: #C08478; flex: 0 0 auto; }

  /* ---------- Report body shared styles ---------- */
  .report-meta {
    margin: 14px 0 4px 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .report-meta .block .label {
    font-size: 9px;
    color: #C08478;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .report-meta .block .v { color: #1A1A1A; font-size: 12px; font-weight: 700; }
  .report-meta .block .meta-line { color: #444; font-size: 10.5px; }
  .report-meta .right { text-align: right; }
  .report-meta .id-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 4px 12px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #E5DAD5;
  }
  .report-meta .id-grid .cell .k {
    color: #888;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 8.5px;
    display: block;
    margin-bottom: 1px;
  }
  .report-meta .id-grid .cell .v {
    color: #1A1A1A;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 10px;
  }

  /* Reusable report KPI strip */
  .kpi-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 14px 0 8px 0;
  }
  .kpi-strip .kpi {
    flex: 1 1 140px;
    padding: 8px 12px;
    background: #FBF8F6;
    border-left: 3px solid #C08478;
    border-radius: 2px;
  }
  .kpi-strip .kpi .k {
    color: #888;
    font-size: 8.5px;
    letter-spacing: 1px;
    text-transform: uppercase;
    font-weight: 600;
    display: block;
    margin-bottom: 2px;
  }
  .kpi-strip .kpi .v { color: #1A1A1A; font-size: 13px; font-weight: 700; }
  .kpi-strip .kpi.danger { border-left-color: #E76F51; background: #FBE9E4; }
  .kpi-strip .kpi.success { border-left-color: #2A9D8F; background: #E9F5F3; }
  .kpi-strip .kpi.accent { border-left-color: #264653; background: #F0F4F5; }

  /* Payment Breakdown — mirrors the on-screen PaymentBreakdownPanel so
     PDF/physical prints carry the same reconciliation information.
     Uses its OWN .pb-table (NOT .report-table) so the warm cream
     palette is not overridden by the invoice-table dark header/footer. */
  .payment-breakdown {
    margin: 14px 0 18px 0;
    page-break-inside: avoid;
  }
  .payment-breakdown h3 {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: #C08478;
    margin: 0 0 8px 0;
  }
  table.pb-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    background: #fff;
  }
  table.pb-table thead th {
    background: #FBF8F6;
    color: #6B7280;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
    padding: 6px 10px;
    border-bottom: 1px solid #EFE5E1;
    text-align: left;
  }
  table.pb-table thead th.num { text-align: right; }
  table.pb-table tbody td {
    padding: 7px 10px;
    color: #1A1A1A;
    border-bottom: 1px solid #F5F0EE;
    background: #fff;
  }
  table.pb-table tbody td.num,
  table.pb-table tfoot td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: 'Courier New', monospace;
  }
  table.pb-table td.pct { color: #999; font-size: 10px; width: 60px; }
  /* Inline horizontal bar column — visualises the row's share of the total.
     The bar sits in its own <td class="bar"> between amount and % so the
     percentage remains numerically readable while stakeholders can eyeball
     the payment-mix ratios at a glance. */
  table.pb-table td.bar { width: 32%; padding: 7px 12px; vertical-align: middle; }
  table.pb-table td.bar .bar-track {
    height: 6px;
    background: #F1EAE6;
    border-radius: 3px;
    overflow: hidden;
    position: relative;
  }
  table.pb-table td.bar .bar-fill {
    height: 100%;
    border-radius: 3px;
    background: #C08478;
  }
  /* Total Paid row — separator + bold, still light background */
  table.pb-table tfoot tr.pb-paid td {
    background: #fff;
    color: #1A1A1A;
    font-weight: 700;
    padding: 8px 10px;
    border-top: 1px solid #E5E5E5;
    border-bottom: 1px solid #F5F0EE;
  }
  /* Outstanding Due — accent red for both label and amount */
  table.pb-table tfoot tr.pb-due td {
    background: #FEF6F3;
    color: #E76F51;
    font-weight: 700;
    padding: 7px 10px;
    border-bottom: 1px solid #F5F0EE;
  }
  /* Grand Total — subtle cream band, thicker top border, largest weight */
  table.pb-table tfoot tr.pb-grand td {
    background: #FBF8F6;
    color: #1A1A1A;
    font-weight: 800;
    font-size: 12px;
    padding: 9px 10px;
    border-top: 2px solid #1A1A1A;
    border-bottom: none;
  }
  table.pb-table tfoot tr.pb-grand td.pct { color: #999; font-weight: 500; }

  /* Highlighted "Reste à régler / Solde de la Période" callouts */
  .callouts {
    display: flex;
    gap: 10px;
    margin-top: 12px;
  }
  .callouts .callout {
    flex: 1;
    padding: 12px 14px;
    border-radius: 4px;
    background: #1A1A1A;
    color: #fff;
  }
  .callouts .callout.amber { background: #C08478; }
  .callouts .callout .k {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    opacity: 0.8;
    font-weight: 600;
    display: block;
  }
  .callouts .callout .v {
    font-size: 17px;
    font-weight: 700;
    margin-top: 2px;
  }

  /* Tables — match invoice styling for consistency */
  table.report-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.report-table thead { display: table-header-group; }
  table.report-table th {
    background: #1A1A1A;
    color: #fff;
    text-align: left;
    padding: 7px 8px;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  table.report-table th.num { text-align: right; }
  table.report-table td {
    padding: 7px 8px;
    border-bottom: 1px solid #EFE5E1;
    font-size: 10.5px;
    vertical-align: top;
  }
  table.report-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.report-table tbody tr { page-break-inside: avoid; }
  table.report-table tbody tr:nth-child(even) td { background: #FBF8F6; }
  table.report-table tr.overdue td { background: #FBE9E4 !important; color: #C0392B; }
  table.report-table tfoot td {
    background: #1A1A1A;
    color: #fff;
    font-weight: 700;
    border: 0;
    padding: 8px;
    font-size: 11px;
  }
  table.report-table tfoot td.num { text-align: right; }

  /* Legal footer — bottom of last page only. Inside a flex-column body so
     margin-top:auto pushes it to the page bottom regardless of content size. */
  .legal-footer {
    margin-top: auto;
    padding: 12px 16px 4px 16px;
    border-top: 1.5px solid #1A1A1A;
    background: #fff;
    font-size: 8.5px;
    line-height: 1.55;
    color: #444;
    text-align: center;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    page-break-inside: avoid;
  }
  .legal-footer .l1 { font-weight: 700; color: #1A1A1A; }
  .legal-footer .l2, .legal-footer .l3 { color: #555; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body onload="window.print()">

<table class="page-frame">
  <thead>
    <tr>
      <td>
        <div class="page-header">
          <div class="hdr-top">
            <div class="brand">
              <img src="${LOGO_URL}" alt="UNIVERS COSMETIC" />
              <div class="brand-text">
                <h1>UNIVERS <span>COSMETIC</span></h1>
                <div class="tagline">${COMPANY.tagline}</div>
              </div>
            </div>
            <div class="doc-meta">
              <div class="title">${title}</div>
              ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ''}
              <div class="gen">${new Date().toLocaleString(language === 'fr' ? 'fr-FR' : 'en-GB')}</div>
            </div>
          </div>

          <div class="info-strip">
            <div class="group">
              <span class="item">${ICON.facebook}<span>${COMPANY.facebook}</span></span>
              <span class="item">${ICON.instagram}<span>${COMPANY.instagram}</span></span>
            </div>
            <div class="group">
              <span class="item">${ICON.phone}<span>${COMPANY.phone}</span></span>
              <span class="item">${ICON.email}<span>${COMPANY.email}</span></span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>
        ${metaHtml}
        ${contentHtml}
      </td>
    </tr>
  </tbody>
</table>

<div class="legal-footer">
  <div class="l1">${COMPANY.legal_line_1}</div>
  <div class="l2">${COMPANY.legal_line_2}</div>
  <div class="l3">${COMPANY.legal_line_3}</div>
</div>

</body>
</html>`;
}

/** Open a new tab and trigger the browser print dialog with the branded chrome. */
export function printReportDocument(opts) {
    const html = buildReportHtml(opts);
    const w = openPrintWindow(html, { width: 1000, height: 1100 });
    if (!w) return;
}
